import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Ban, DollarSign, MoreVertical, Pause, Play, Trash2 } from 'lucide-react';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Switch } from '@repo/ui/src/components/ui/switch';
import { cn } from '@repo/ui/src/lib/utils';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import type { ClassColor, EnrollmentWithSubject } from '@repo/shared-types';
import { useClasses } from '../../../hooks/useClasses';
import { classColorStyle } from '../../../lib/classColors';
import { groupSlotsByTime } from '../../../lib/classSchedule';
import { useDeleteEnrollment, useRenewTerm, useStartTerm, useUpdateEnrollment } from '../../../hooks/useEnrollments';
import { useCan } from '../../../hooks/useCan';
import { AdminOnlyTooltip } from '../../../components/AdminOnlyTooltip';
import { DatePicker } from '../../../components/pickers/DatePicker';
import { enrollmentKhoaState } from '../../../lib/enrollmentKhoa';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Card action sizing (design 13 §4 + §6): 38.4px full-width thumb targets on a
 *  phone, 28.8px compact buttons from md up. Pinned in px — --spacing is 0.2rem,
 *  so `size="sm"` alone lands at 32px at both widths. */
const CARD_ACTION_CLASS = 'h-[38.4px] flex-1 md:h-[28.8px] md:flex-none md:px-[11.2px]';
const CARD_MENU_CLASS = 'h-[38.4px] w-[38.4px] shrink-0 md:h-[28.8px] md:w-[28.8px]';

/** 8px colour square (spec §7, S7/1): filled tint+ink when the class has a
 *  colour, a dashed hollow square when it doesn't (a single-khung-giờ class,
 *  or the colour-assign rule hasn't reached it yet) — same convention as
 *  `ClassListTab`'s own `ColorSquare`, just sized for this denser line. */
function ScheduleColorSquare({ color }: { color: ClassColor | null }) {
  const style = classColorStyle(color);
  if (!style) {
    return <span className="h-2 w-2 shrink-0 rounded-[2px] border border-dashed border-surface-300" aria-hidden />;
  }
  return <span className="h-2 w-2 shrink-0 rounded-[2px] border opacity-90" style={style} aria-hidden />;
}

/**
 * The classes an enrollment belongs to, one line per class (S7/1, spec §5
 * "Student page"): colour square, `displayName` linking to `/classes/:id`,
 * that class's own weekday·time text, and a "2/3 khung giờ" pill when the
 * enrollment sits in only some of the class's khung giờ.
 *
 * `enrollment.classes` (Task 6's chips) already carries `id`/`displayName`/
 * `color`/`joinedSlots`/`totalSlots`, but not the khung giờ themselves — those
 * come from `useClasses({ subjectId })`, matched back to each chip by id.
 * React Query dedupes this against any other caller asking for the same
 * subject's classes (e.g. AssignDialog), so this is the "one lookup shared
 * by the page" the spec asks for without having to thread the data down as
 * a prop.
 */
function EnrollmentSchedule({ enrollment }: { enrollment: EnrollmentWithSubject }) {
  const { t: tScheduling } = useTranslation('scheduling');
  const { data: classRows } = useClasses({ subjectId: enrollment.subjectId, status: 'ACTIVE' });
  const chips = enrollment.classes ?? [];

  if (chips.length === 0) {
    // Same words the card used to show as its start-date fallback: an
    // unassigned enrollment has no schedule *and* no start, and the schedule
    // is the more useful thing to name — the badge in Tier 1 now carries the
    // same message visually, this stays as the line's own empty state.
    return <p className="text-sm text-muted-foreground">{tScheduling('assign.notAssigned')}</p>;
  }

  return (
    <ul className="flex flex-col gap-1">
      {chips.map((c) => {
        const row = classRows?.find((r) => r.id === c.id);
        const groups = row ? groupSlotsByTime(row.slots) : [];
        const scheduleText = groups
          .map((g) => `${g.days.map((d) => tScheduling(`days.${d}`)).join(', ')} ${g.time}`)
          .join(' · ');
        const partial = c.joinedSlots < c.totalSlots;
        return (
          <li key={c.id}>
            <Link
              to={`/classes/${c.id}`}
              className="flex flex-wrap items-center gap-2 text-[13.5px] text-surface-800 hover:underline"
            >
              <ScheduleColorSquare color={c.color} />
              <span className="font-semibold">
                <span className="hidden sm:inline">{tScheduling('assign.classPrefix', { name: c.displayName })}</span>
                <span className="sm:hidden">{c.displayName}</span>
              </span>
              {scheduleText && <span className="text-surface-500">· {scheduleText}</span>}
              {partial && (
                <>
                  <span className="hidden h-5 items-center rounded-full bg-surface-100 px-1.5 text-[11.5px] font-semibold tabular-nums text-surface-600 sm:inline-flex">
                    {tScheduling('assign.classPartial', { joined: c.joinedSlots, total: c.totalSlots })}
                  </span>
                  <span className="text-[11.5px] text-surface-500 sm:hidden">
                    ({c.joinedSlots}/{c.totalSlots})
                  </span>
                </>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export function EnrollmentCard({
  enrollment: e, studentId, onAssign,
}: {
  enrollment: EnrollmentWithSubject;
  studentId: string | null;
  onAssign: () => void;
}) {
  const { t } = useTranslation('students');
  const { t: tScheduling } = useTranslation('scheduling');
  const { mutate: update } = useUpdateEnrollment(e.id);
  const { mutate: delEnrollment, isPending: isDeleting } = useDeleteEnrollment();
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [editRateOpen, setEditRateOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const canManageEnrollments = useCan('manage:enrollments');
  const canManageSlots = useCan('manage:slots');

  const isEnded = e.status === 'ENDED';
  const isPaused = e.status === 'PAUSED';
  const khoa = enrollmentKhoaState(e);
  // S7/1 + handoff decision 12: no class membership is the one loose end a
  // card can have, so it earns the attention badge AND turns "Xếp lớp" into
  // the card's primary action (outline otherwise, unchanged from today).
  const hasClass = (e.classes?.length ?? 0) > 0;

  const pause = () =>
    update(
      { status: 'PAUSED' },
      {
        onSuccess: () => {
          setPauseConfirmOpen(false);
          toast.success(t('enrollment.actions.pauseSuccess'));
        },
      },
    );
  const resume = () =>
    update({ status: 'ACTIVE' }, { onSuccess: () => toast.success(t('enrollment.actions.resumeSuccess')) });
  const handleEnd = () =>
    update(
      { status: 'ENDED' },
      {
        onSuccess: () => {
          setEndConfirmOpen(false);
          toast.success(t('enrollment.actions.endSuccess'));
        },
      },
    );
  const handleDelete = () => delEnrollment(e.id, { onSuccess: () => setDeleteConfirmOpen(false) });

  const badge =
    khoa.state === 'nearlyOut' ? (
      <Badge variant="warning">{t('term.khoa.badgeNearlyOut', { count: khoa.remaining })}</Badge>
    ) : khoa.state === 'out' ? (
      <Badge variant="danger">{t('term.khoa.badgeOut')}</Badge>
    ) : khoa.state === 'paused' ? (
      <Badge variant="muted">{t('enrollment.statusPaused')}</Badge>
    ) : khoa.state === 'ended' ? (
      <Badge variant="muted">{t('enrollment.statusEnded')}</Badge>
    ) : null;

  // Tier 3, the two live numbers. For a khoá enrollment the progress comes from
  // `termState` (consumed/termSize), not `attendedCount`/`termSize`: the two
  // disagreed, and the old card printed BOTH — `attendedCount` on this line and
  // `termState.consumed` on the `term.state` line right below it. The term's own
  // counter is the one the khoá is billed on, so it is the one that survives.
  const progress =
    e.termState && e.termState.hasOpenTerm
      ? t('enrollment.termProgress', { consumed: e.termState.consumed, termSize: e.termState.termSize })
      : e.termState
        ? t('term.notStarted')
        : t('enrollment.monthlyProgress', { count: e.attendedCount });

  // Rendered twice, switched by CSS: on a phone the actions are a full-width row
  // at the bottom of the card (thumb reach — 13 §6), on desktop they sit in the
  // title row. `useIsMobile()` is false on its first render, so branching on it
  // would flash the desktop layout; the hidden copy is display:none and out of
  // the accessibility tree. This supersedes the card's previous "actions share
  // the title row at every width" note — that was written when the row held two
  // controls, and it now holds three plus a name plus a khoá badge.
  const actions = (
    <>
      {/* PAUSED hides this (13 §4): the only enrollment action offered for a
          paused khoá is "Tiếp tục" in ⋮, so there is nothing to renew inline
          until it is active again. */}
      {e.termState && !isEnded && !isPaused && (
        <TermStateActions
          enrollment={e}
          studentId={studentId}
          variant={khoa.state === 'out' ? 'default' : 'outline'}
        />
      )}
      {!isEnded && (
        <AdminOnlyTooltip show={!canManageSlots} className="flex flex-1 md:flex-none">
          <Button
            variant={hasClass ? 'outline' : 'default'}
            size="sm"
            className={CARD_ACTION_CLASS}
            onClick={onAssign}
            disabled={!canManageSlots}
          >
            {tScheduling('assign.title')}
          </Button>
        </AdminOnlyTooltip>
      )}
      <AdminOnlyTooltip show={!canManageEnrollments}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={CARD_MENU_CLASS}
              aria-label={t('enrollment.actions.menu')}
              disabled={!canManageEnrollments}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {e.status === 'ACTIVE' && (
              <DropdownMenuItem onClick={() => setPauseConfirmOpen(true)}>
                <Pause className="h-4 w-4" /> {t('enrollment.actions.pause')}
              </DropdownMenuItem>
            )}
            {(isPaused || isEnded) && (
              <DropdownMenuItem onClick={resume}>
                <Play className="h-4 w-4" /> {t('enrollment.actions.resume')}
              </DropdownMenuItem>
            )}
            {!isEnded && (
              <DropdownMenuItem onClick={() => setEndConfirmOpen(true)}>
                <Ban className="h-4 w-4" /> {t('enrollment.actions.end')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setEditRateOpen(true)}>
              <DollarSign className="h-4 w-4" /> {t('enrollment.actions.editRate')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600"
              disabled={isDeleting}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              <Trash2 className="h-4 w-4" /> {t('enrollment.actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </AdminOnlyTooltip>
    </>
  );

  // Four tiers (13 §4): identity + status, the week, the two live numbers, and
  // everything reference-only collapsed into one muted footer.
  return (
    <div className="flex flex-col gap-2 rounded-md border border-surface-200 bg-card px-4 py-[12.8px]">
      {/* Tier 1 */}
      <div className="flex items-start justify-between gap-[12.8px]">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold text-surface-800">{e.subject?.name}</span>
          {!isEnded && !hasClass && <Badge variant="warning">{t('enrollment.notAssignedBadge')}</Badge>}
          {badge}
        </div>
        <div
          className={cn(
            'shrink-0 items-center gap-[6.4px]',
            // ENDED strips `actions` down to just the ⋮ (TermStateActions and
            // Assign both gate on `!isEnded`) — too little to earn its own
            // full-width phone row below, so the kebab joins the title row at
            // every width instead of leaving that row nearly empty.
            isEnded ? 'flex' : 'hidden md:flex',
          )}
        >
          {actions}
        </div>
      </div>

      {/* Tier 2 — the thing a teacher opens this page to check. */}
      <EnrollmentSchedule enrollment={e} />

      {/* Tier 3 — the two live numbers, in ink so they separate from the footer. */}
      <div className="flex flex-wrap items-baseline gap-x-[12.8px] gap-y-1 text-sm text-surface-800">
        {/* THI-129: money only. The API already nulls the rates for a role
            without `manage:enrollments`; this keeps the slot from rendering as
            an empty span beside the progress text. */}
        {canManageEnrollments && (
          <span className="font-medium">
            {e.pricingType === 'PER_SESSION' && e.ratePerSession != null
              && `${e.ratePerSession.toLocaleString('vi-VN')}${t('subjects.perSession')}`}
            {e.pricingType === 'MONTHLY_FLAT' && e.monthlyRate != null
              && `${e.monthlyRate.toLocaleString('vi-VN')}${t('subjects.perMonth')}`}
          </span>
        )}
        <span className="text-muted-foreground">{progress}</span>
      </div>

      {/* The consequence, in the same words the workbench "Cần chú ý" card uses. */}
      {khoa.state === 'nearlyOut' && (
        <p className="text-xs text-attention-foreground">
          {t('term.khoa.consequenceNearlyOut', { count: khoa.remaining })}
        </p>
      )}
      {khoa.state === 'out' && (
        <p className="text-xs text-destructive">{t('term.khoa.consequenceOut')}</p>
      )}

      {/* Tier 4 — true, occasionally wanted, never glanced at. One muted line;
          the 💳 is gone (13 §4) and the pricing-type label is dropped for
          PER_SESSION/MONTHLY_FLAT because "đ/buổi"/"đ/tháng" above already says
          it, and kept for TERM where it carries the term size. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-surface-400">
        {e.pricingType === 'PER_SESSION' && e.billingPeriod === 'TERM' && e.termSize != null && (
          <span>
            {e.termState?.currentLabel
              ? t('enrollment.termRefWithLabel', { size: e.termSize, label: e.termState.currentLabel })
              : t('enrollment.termRef', { size: e.termSize })}
          </span>
        )}
        {e.startDate && (
          <span>{t('enrollment.startDate', { date: new Date(e.startDate).toLocaleDateString('vi-VN') })}</span>
        )}
        {!(e.pricingType === 'PER_SESSION' && e.billingPeriod === 'TERM') && (
          <span>{t('enrollment.monthlyBillingHint')}</span>
        )}
      </div>

      {!isEnded && <div className="flex items-center gap-[6.4px] md:hidden">{actions}</div>}

      <AlertDialog open={pauseConfirmOpen} onOpenChange={setPauseConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('enrollment.actions.pauseConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('enrollment.actions.pauseConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('enrollment.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={pause}>{t('enrollment.actions.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={endConfirmOpen} onOpenChange={setEndConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('enrollment.actions.endConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('enrollment.actions.endConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>{t('enrollment.actions.endBullet1')}</li>
            <li>{t('enrollment.actions.endBullet2')}</li>
            <li>{t('enrollment.actions.endBullet3')}</li>
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('enrollment.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleEnd} className="bg-red-600 hover:bg-red-700">
              {t('enrollment.actions.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('enrollment.actions.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('enrollment.actions.deleteConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('enrollment.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
              {t('enrollment.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditRateDialog enrollment={e} open={editRateOpen} onOpenChange={setEditRateOpen} />
    </div>
  );
}

function EditRateDialog({
  enrollment, open, onOpenChange,
}: {
  enrollment: EnrollmentWithSubject;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation('students');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader><DialogTitle>{t('enrollment.editRateDialog.title')}</DialogTitle></DialogHeader>
        {/* Mounted only while open so its rate input always initializes fresh from the current rate
            (no effect-driven resync needed). */}
        {open && <EditRateForm enrollment={enrollment} onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
}

function EditRateForm({
  enrollment, onOpenChange,
}: {
  enrollment: EnrollmentWithSubject;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation('students');
  const { mutate: update, isPending } = useUpdateEnrollment(enrollment.id);
  const currentRate = enrollment.pricingType === 'PER_SESSION' ? enrollment.ratePerSession : enrollment.monthlyRate;
  const [rate, setRate] = useState(currentRate != null ? String(currentRate) : '');
  const [chargeAbsent, setChargeAbsent] = useState(enrollment.chargeAbsent);
  const isPerSession = enrollment.pricingType === 'PER_SESSION';

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const amt = Number(rate);
    const payload = isPerSession ? { ratePerSession: amt, chargeAbsent } : { monthlyRate: amt };
    update(payload, {
      onSuccess: () => {
        onOpenChange(false);
        toast.success(t('enrollment.editRateDialog.success'));
      },
    });
  };

  const label = isPerSession ? t('enrollment.ratePerSession') : t('enrollment.monthlyRate');

  return (
    <form onSubmit={submit}>
      <div className="grid gap-2 py-2">
        <Label htmlFor="editRate">{label}</Label>
        <Input id="editRate" type="number" min="0" value={rate} onChange={(ev) => setRate(ev.target.value)} required />
        <p className="text-xs text-muted-foreground">{t('enrollment.editRateDialog.description')}</p>
        {/* chargeAbsent has no meaning for MONTHLY_FLAT — it's forced false at creation. */}
        {isPerSession && (
          <div className="flex items-center gap-2 pt-2">
            <Switch id="editChargeAbsent" checked={chargeAbsent} onCheckedChange={setChargeAbsent} />
            <Label htmlFor="editChargeAbsent" className="font-normal">
              {t('enrollment.editRateDialog.chargeAbsentLabel')}
            </Label>
          </div>
        )}
        {isPerSession && enrollment.billingPeriod === 'TERM' && (
          <p className="text-xs text-amber-600">{t('enrollment.editRateDialog.chargeAbsentTermHint')}</p>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('enrollment.editRateDialog.cancel')}
        </Button>
        <Button type="submit" disabled={isPending || !rate}>
          {isPending ? t('enrollment.editRateDialog.submitting') : t('enrollment.editRateDialog.submit')}
        </Button>
      </DialogFooter>
    </form>
  );
}

function TermStateActions({
  enrollment, studentId, variant = 'outline',
}: {
  enrollment: EnrollmentWithSubject;
  studentId: string | null;
  /** `default` (primary) only when the khoá is spent — the attention system's
   *  rule: already-out earns the loud button, nearly-out does not. */
  variant?: 'default' | 'outline';
}) {
  const { t } = useTranslation('students');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [startDate, setStartDate] = useState(todayIso());
  const { mutate: startTerm, isPending: isStarting } = useStartTerm(enrollment.id, studentId ?? undefined);
  const { mutate: renewTerm, isPending: isRenewing } = useRenewTerm(enrollment.id, studentId ?? undefined);
  const canManageEnrollments = useCan('manage:enrollments');

  // Reset the picker to today each time the confirm dialog (re)opens. Adjusting state during
  // render (rather than an effect) per React's guidance for resetting state on a prop change:
  // https://react.dev/learn/you-might-not-need-an-effect
  const [prevConfirmOpen, setPrevConfirmOpen] = useState(confirmOpen);
  if (confirmOpen !== prevConfirmOpen) {
    setPrevConfirmOpen(confirmOpen);
    if (confirmOpen) setStartDate(todayIso());
  }

  if (!enrollment.termState) return null;
  const hasOpenTerm = enrollment.termState.hasOpenTerm;
  const isPending = isStarting || isRenewing;

  const handleConfirm = () => {
    if (hasOpenTerm) {
      renewTerm(undefined, { onSuccess: () => setConfirmOpen(false) });
    } else {
      startTerm(startDate, { onSuccess: () => setConfirmOpen(false) });
    }
  };

  return (
    <>
      <AdminOnlyTooltip show={!canManageEnrollments} className="flex flex-1 md:flex-none">
        <Button
          variant={variant}
          size="sm"
          className={cn(CARD_ACTION_CLASS)}
          onClick={() => setConfirmOpen(true)}
          disabled={isPending || !canManageEnrollments}
        >
          {hasOpenTerm ? t('term.renew') : t('term.start')}
        </Button>
      </AdminOnlyTooltip>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{hasOpenTerm ? t('term.renewConfirmTitle') : t('term.startConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {hasOpenTerm ? t('term.renewConfirmDescription') : t('term.startConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!hasOpenTerm && (
            <div className="grid gap-1">
              <Label htmlFor="termStartDate" className="text-xs">{t('term.startDateLabel')}</Label>
              <DatePicker id="termStartDate" className="max-w-[200px]" value={startDate} onChange={setStartDate} />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>{t('term.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={isPending}>
              {t('term.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
